import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { formatByCurrency } from "../../../utils/formatCurrency";

// Floating horizontal bars on a shared price axis: each row spans from its own
// min to its own max, so overlap and undercutting are visible at a glance in a
// way a plain min/max table can't show.
export default function PriceRangeChart({ own, competitors = [], currency }) {
  const { t } = useTranslation();

  const rows = useMemo(() => {
    const all = [];
    if (own?.price_range_min != null || own?.price_range_max != null) {
      all.push({
        key: "own",
        label: t("admin.insights.competitorIntelligence.ourProducts"),
        min: own.price_range_min ?? own.price_range_max,
        max: own.price_range_max ?? own.price_range_min,
        isOwn: true,
      });
    }
    competitors.forEach((competitor) => {
      if (competitor.price_range_min == null && competitor.price_range_max == null) return;
      all.push({
        key: `c-${competitor.competitor_id}`,
        label: competitor.competitor_name,
        min: competitor.price_range_min ?? competitor.price_range_max,
        max: competitor.price_range_max ?? competitor.price_range_min,
        isOwn: false,
      });
    });
    return all;
  }, [own, competitors, t]);

  const bounds = useMemo(() => {
    if (!rows.length) return null;
    const lo = Math.min(...rows.map((row) => row.min));
    const hi = Math.max(...rows.map((row) => row.max));
    // Guard the degenerate single-value case so every bar doesn't collapse to 0 width.
    const span = hi - lo || hi || 1;
    return { lo, hi, span };
  }, [rows]);

  if (!rows.length) {
    return (
      <div className="dpi-chart-empty">
        {t("admin.insights.competitorIntelligence.noPriceData")}
      </div>
    );
  }

  return (
    <div className="dpi-pr-chart">
      {rows.map((row) => {
        const leftPct = ((row.min - bounds.lo) / bounds.span) * 100;
        const widthPct = Math.max(((row.max - row.min) / bounds.span) * 100, 1.5);
        return (
          <div key={row.key} className="dpi-pr-row">
            <span className={`dpi-pr-label${row.isOwn ? " dpi-pr-label--own" : ""}`}>
              {row.label}
            </span>
            <div className="dpi-pr-track">
              <div
                className={`dpi-pr-bar${row.isOwn ? " dpi-pr-bar--own" : ""}`}
                style={{ left: `${leftPct}%`, width: `${widthPct}%` }}
              />
            </div>
            <span className="dpi-pr-value">
              {row.min === row.max
                ? formatByCurrency(row.min, currency)
                : `${formatByCurrency(row.min, currency)} – ${formatByCurrency(row.max, currency)}`}
            </span>
          </div>
        );
      })}
    </div>
  );
}
